/**
 * Description Field Type
 *
 * Provides configuration and renderers for description/textarea fields:
 * - Read-only column renderer (displays text with ellipsis)
 * - Editable column renderer (uses single-line text input for inline editing)
 * - Form field renderer (uses multiline textarea for modals)
 *
 * Inline editing uses a single-line input (Enter to save) for better UX.
 * Form modals use a multiline textarea for longer content entry.
 */

import type { ColumnDef, CellContext } from '@tanstack/react-table';
import { TextField, Box } from '@mui/material';
import { DescriptionCellContainer, DescriptionText, descriptionFormSx } from '../../styles';
import { TableTooltip, EditableDescriptionCell } from '../../components';
import type {
  FieldTypeConfig,
  FormFieldProps,
  ColumnRenderer,
  RenderOptions,
  CustomColumn,
} from '../../types';
import { getCharacterLimit, defaultDescriptionMaxChar, validateFieldValue } from '../../validation';
import { CharacterCounter } from './characterCounter';
import { FieldError, shouldShowFieldError } from './fieldError';
import { XL } from './columnWidths';
import { ClearFieldButton, ClearFieldFocusContainer } from '../../components/ClearFieldButton';
import { ClearedFieldIndicator } from '../../components/ClearedFieldIndicator';
import { getBulkEditClearState } from '../../hooks/useBulkEditClear';

// ============================================================================
// Editable Column Renderer
// ============================================================================

// Import shared cell value utility
import { getCellStringValue } from './cellValue';

/** Re-export for backwards compatibility (source of truth is validation/fieldSchemas). */
export { defaultDescriptionMaxChar } from '../../validation';

// ============================================================================
// Read-Only Column Renderer
// ============================================================================

/**
 * Description read-only renderer - displays text in a cell container
 * Uses getValue() to support nested accessors
 */
export const descriptionReadOnlyRenderer = <T extends { id: number | string }>(
  _col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => ({
  cell: (cellContext: CellContext<T, unknown>) => {
    const value = cellContext.getValue();
    const displayValue = String(value ?? '');
    return (
      <TableTooltip title={displayValue}>
        <DescriptionCellContainer>
          <DescriptionText>{displayValue}</DescriptionText>
        </DescriptionCellContainer>
      </TableTooltip>
    );
  },
});

// ============================================================================
// Form Field Renderer
// ============================================================================

/**
 * Render a textarea field for forms
 * Character count {currentChar}/{maxChar} at bottom-left; resize handle stays bottom-right.
 */
export const renderDescriptionField = ({
  col,
  value,
  valueForValidation,
  onChange,
  isFirst,
  disabled,
  inputRef,
  bulkEditContext,
  validationContext,
}: FormFieldProps): React.ReactNode => {
  const emptyDisplayNode = (col as { emptyDisplayNode?: React.ReactNode }).emptyDisplayNode;
  const valueEmpty = value == null || value === '';
  const showEmptyChip = valueEmpty && emptyDisplayNode;

  const maxChar = getCharacterLimit(col) ?? defaultDescriptionMaxChar;
  const length = String(value ?? '').length;
  const isAtLimit = length >= maxChar;

  // Use Zod validation to get error message (valueForValidation for bulk-edit "keep current")
  const valueToValidate = valueForValidation !== undefined ? valueForValidation : value;
  const validation = validateFieldValue({ ...col, type: 'description' }, valueToValidate);
  const errorMessage = !validation.success ? validation.error : undefined;
  const showError = shouldShowFieldError(col.key, validationContext);

  const {
    isBulkEdit,
    isCleared,
    showClearButton,
    handleBulkEditChange,
    handleClear,
    handleUndoClear,
  } = getBulkEditClearState({
    value,
    valueForValidation,
    valueEmpty,
    colKey: col.key,
    required: (col as { required?: boolean }).required,
    onChange,
    bulkEditContext,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange(col.key, handleBulkEditChange(e.target.value));
    validationContext?.onFieldTouched?.(col.key);
  };

  if (isBulkEdit && isCleared) {
    return (
      <Box key={col.key}>
        <ClearedFieldIndicator fieldName={col.label} onClick={handleUndoClear} />
      </Box>
    );
  }

  return (
    <ClearFieldFocusContainer>
      <Box sx={{ position: 'relative' }}>
        <TextField
          key={col.key}
          label={col.label}
          placeholder={showEmptyChip ? '' : col.placeholder}
          fullWidth
          disabled={disabled}
          inputRef={isFirst ? inputRef : undefined}
          autoFocus={isFirst}
          value={(value as string) || ''}
          onChange={handleChange}
          variant="outlined"
          multiline
          rows={4}
          inputProps={{ maxLength: maxChar }}
          InputLabelProps={{ shrink: true }}
          sx={descriptionFormSx}
          InputProps={showEmptyChip ? { startAdornment: emptyDisplayNode } : undefined}
        />
        <CharacterCounter currentLength={length} maxChar={maxChar} isError={isAtLimit} />
      </Box>
      <ClearFieldButton onClick={handleClear} visible={showClearButton} />
      <FieldError error={errorMessage} show={showError} />
    </ClearFieldFocusContainer>
  );
};

/**
 * Description editable renderer - unified pattern with single-line input
 * Uses col.id as the data field, auto-infers update
 * Supports conditional editability via editable: (cell) => boolean
 *
 * Note: Uses CellInput (single-line) for inline editing so Enter saves the value.
 * Form modals use a multiline textarea via renderDescriptionField.
 */
export const descriptionEditableRenderer = <T extends { id: number | string }>(
  col: CustomColumn<T>,
  _options: RenderOptions<T>
): Partial<ColumnDef<T>> => {
  const editableProp = col.editable;

  return {
    cell: (context: CellContext<T, unknown>) => {
      const value = getCellStringValue(context, col.id);
      const isRowEditable =
        typeof editableProp === 'function' ? editableProp(context) : editableProp === true;

      if (!isRowEditable) {
        return (
          <TableTooltip title={value}>
            <DescriptionCellContainer>
              <DescriptionText>{value}</DescriptionText>
            </DescriptionCellContainer>
          </TableTooltip>
        );
      }

      return (
        <EditableDescriptionCell
          context={context}
          value={value || ''}
          onUpdate={newValue => {
            if (col.onUpdate) {
              return col.onUpdate(context.row.original, {
                [col.id]: newValue,
              } as Partial<T>);
            }
          }}
          placeholder={col.placeholder}
        />
      );
    },
    meta: {
      editable: true,
    },
  };
};

// ============================================================================
// Field Type Configuration
// ============================================================================

/**
 * Description field type configuration
 */
export const descriptionFieldType: FieldTypeConfig = {
  placeholder: (label: string) => `Enter ${label.toLowerCase()}...`,
  ...XL,
};

// ============================================================================
// Typed Renderer Exports (for registry)
// ============================================================================

/** Read-only renderer with proper typing */
export const descriptionReadOnly: ColumnRenderer = descriptionReadOnlyRenderer;

/** Editable renderer with proper typing */
export const descriptionEditable: ColumnRenderer = descriptionEditableRenderer;
